// src/hooks/useAuxiliaryData.js (CORRIGIDO)

import { useState, useEffect, useCallback, useMemo } from 'react';
import { db } from '../firebaseConfig';
import { collection, onSnapshot, getDocs, query, where } from 'firebase/firestore';

const ITEMS_COLLECTION = 'raw-materials';
const CATEGORY_COLLECTION_PATH = 'parameters/data/product-categories';
const PRODUCT_TYPES_COLLECTION = 'parameters/data/product-types';
const PROCEDURES_COLLECTION = 'procedures';

export const useAuxiliaryData = () => {
    const [allItems, setAllItems] = useState([]);
    const [allCategories, setAllCategories] = useState([]);
    const [allProductTypes, setAllProductTypes] = useState([]);
    const [procedures, setProcedures] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isInitialized, setIsInitialized] = useState(false);
    const [loadErrors, setLoadErrors] = useState({});

    // ============================================================
    // CORREÇÃO 1: Melhor detecção de matérias-primas
    // ============================================================
    const allRawMaterials = useMemo(() => {
        if (!allItems.length) {
            console.warn('⚠️ allItems está vazio! Verifique a coleção raw-materials no Firestore.');
            return [];
        }

        // Múltiplas estratégias para identificar matérias-primas
        const materials = allItems.filter(item => {
            // Estratégia 1: Campo type explícito
            if (item.type === 'raw-material' || item.type === 'MP') return true;
            if (item.isRawMaterial === true) return true;

            // Estratégia 2: Por categoria (se disponível)
            if (item.category === 'raw-material' || item.category === 'MP') return true;

            // Estratégia 3: Por código (MP = Matéria Prima)
            if (item.code && (item.code.startsWith('MP') || item.code.startsWith('MP-'))) return true;

            // Estratégia 4: Se tem categoryIds, verifica se alguma categoria é de matéria-prima
            if (item.categoryIds && item.categoryIds.length > 0 && allCategories.length > 0) {
                const hasMPCategory = item.categoryIds.some(catId => {
                    const category = allCategories.find(c => c.id === catId);
                    return category && (category.name === 'MATÉRIA PRIMA' || category.name === 'MP');
                });
                if (hasMPCategory) return true;
            }

            // Estratégia 5: Se é água (caso especial)
            if (item.code === 'MP001') return true;

            return false;
        });

        return materials;
    }, [allItems, allCategories]);

    // ============================================================
    // CORREÇÃO 2: Normalizar nomes de categorias
    // ============================================================
    const normalizedCategories = useMemo(() => {
        return allCategories.map(cat => ({
            ...cat,
            normalizedName: cat.name?.toUpperCase().trim() || ''
        }));
    }, [allCategories]);

    // ============================================================
    // Listener para items (com logging)
    // ============================================================
    useEffect(() => {
        let isMounted = true;

        const unsubscribe = onSnapshot(
            collection(db, ITEMS_COLLECTION),
            (snapshot) => {
                if (!isMounted) return;
                const items = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
                setAllItems(items);
            },
            (error) => {
                console.error('❌ Erro no listener de items:', error);
                setLoadErrors(prev => ({ ...prev, items: error.message }));
            }
        );

        return () => {
            isMounted = false;
            unsubscribe();
        };
    }, []);

    // ============================================================
    // Listener para categorias (com logging)
    // ============================================================
    useEffect(() => {
        let isMounted = true;

        const unsubscribe = onSnapshot(
            collection(db, CATEGORY_COLLECTION_PATH),
            (snapshot) => {
                if (!isMounted) return;
                const categories = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
                setAllCategories(categories);
            },
            (error) => {
                console.error('❌ Erro no listener de categorias:', error);
                setLoadErrors(prev => ({ ...prev, categories: error.message }));
            }
        );

        return () => {
            isMounted = false;
            unsubscribe();
        };
    }, []);

    // Listener para tipos de produto
    useEffect(() => {
        let isMounted = true;

        const unsubscribe = onSnapshot(
            collection(db, PRODUCT_TYPES_COLLECTION),
            (snapshot) => {
                if (!isMounted) return;
                const types = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
                setAllProductTypes(types);
            },
            (error) => {
                console.error('❌ Erro no listener de tipos:', error);
                setLoadErrors(prev => ({ ...prev, productTypes: error.message }));
            }
        );

        return () => {
            isMounted = false;
            unsubscribe();
        };
    }, []);

    // Listener para procedimentos
    useEffect(() => {
        let isMounted = true;

        const unsubscribe = onSnapshot(
            collection(db, PROCEDURES_COLLECTION),
            (snapshot) => {
                if (!isMounted) return;
                const procs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
                setProcedures(procs);
            },
            (error) => {
                console.error('❌ Erro no listener de procedimentos:', error);
                setLoadErrors(prev => ({ ...prev, procedures: error.message }));
            }
        );

        return () => {
            isMounted = false;
            unsubscribe();
        };
    }, []);

    // Inicialização
    useEffect(() => {
        const timeout = setTimeout(() => {
            if (!isInitialized && allItems.length > 0) {
                setIsInitialized(true);
                setIsLoading(false);
            } else if (!isInitialized && !isLoading) {
                // Se passou 10 segundos e ainda não inicializou, força inicialização
                setIsInitialized(true);
                setIsLoading(false);
            }
        }, 10000);

        if (allItems.length > 0 && !isInitialized) {
            setIsInitialized(true);
            setIsLoading(false);
        }

        return () => clearTimeout(timeout);
    }, [allItems, isInitialized, isLoading]);

    return {
        allItems,
        allRawMaterials,
        allCategories: normalizedCategories, // Usar versão normalizada
        allProductTypes,
        procedures,
        isLoading,
        isInitialized,
        loadErrors,
    };
};